import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import { CartActions, StateWithMultiCart } from '@spartacus/cart/base/core'
import { CheckoutDeliveryAddressService } from '@spartacus/checkout/base/core'
import {
  ConverterService,
  InterceptorUtil,
  OccEndpointsService,
  OCC_USER_ID_ANONYMOUS,
  USE_CLIENT_TOKEN
} from '@spartacus/core'
import { Order, ORDER_NORMALIZER } from '@spartacus/order/root'
import { Observable } from 'rxjs'
import { map, take } from 'rxjs/operators'
import { EbCart } from '../../../cart/interface/eb-cart.interface'
import { EbAddress, EndCustomerList, EndCustomerType } from '../../interface/eb-checkout.interface'
import { ENDCUSTOMER_NORMALIZER } from './converter'

@Injectable()
export class EbCheckoutAdapter {
  constructor(
    protected http: HttpClient,
    protected occEndpoints: OccEndpointsService,
    protected converter: ConverterService,
    protected store: Store<StateWithMultiCart>,
    protected checkoutDeliveryAddressService: CheckoutDeliveryAddressService
  ) {}

  public placeOrder(
    userId: string,
    cartId: string,
    termsAndConditions: boolean,
    exportControlProvisions: boolean
  ): Observable<Order> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    })
    if (userId === OCC_USER_ID_ANONYMOUS) {
      headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers)
    }
    const url = this.occEndpoints.buildUrl('placeOrder', {
      urlParams: { userId, cartId },
      queryParams: { termsAndConditions, exportControlProvisions }
    })
    return this.http.post(url, {}, { headers }).pipe(this.converter.pipeable(ORDER_NORMALIZER))
  }

  getTypesOfEndCustomer(userId: string): Observable<EndCustomerType[]> {
    const url = this.occEndpoints.buildUrl('typesOfEndCustomer', {
      urlParams: { userId }
    })
    return this.http.get<EndCustomerList>(url).pipe(
      map(endCustomers => endCustomers.partners),
      this.converter.pipeableMany(ENDCUSTOMER_NORMALIZER)
    )
  }

  setDestinationCountry(
    userId: string,
    cartId: string,
    countryIsoCode: string
  ): Observable<EbCart> {
    const url = this.occEndpoints.buildUrl('setDestinationCountry', {
      urlParams: { userId, cartId },
      queryParams: { targetCountry: countryIsoCode }

    })
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    })
    return this.http.put(url, {}, { headers }).pipe(
      take(1),
      map(newCart => {
        this.store.dispatch(new CartActions.LoadCart({ userId, cartId }))
        return newCart
      })
    )
  }

  setTypeOfEndCustomer(userId: string, cartId: string, partnerCode: string): Observable<EbCart> {
    const url = this.occEndpoints.buildUrl('setTypeOfEndCustomer', {
      urlParams: { userId, cartId }
    })
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    })
    return this.http.put(url, partnerCode, { headers }).pipe(
      take(1),
      map(newCart => {
        this.store.dispatch(new CartActions.LoadCart({ userId, cartId }))
        return newCart
      })
    )
  }

  setShippingAddressChange(
    userId: string,
    cartId: string,
    addressId: string,
    attentionOfText: string
  ): Observable<EbAddress> {
    const url = this.occEndpoints.buildUrl('setShippingAddressChange', {
      urlParams: { userId, addressId }
    })
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    })
    return this.http.post<EbAddress>(url, JSON.stringify(attentionOfText), { headers }).pipe(
      take(1),
      map(newAddress => {
        if (attentionOfText) {
          this.checkoutDeliveryAddressService.setDeliveryAddress(newAddress)
        }
        return newAddress
      })
    )
  }
  addNewEbCommerceAddress(
    userId: string,
    cartId: string,
    newAddress: EbAddress
  ): Observable<EbAddress> {
    const url = this.occEndpoints.buildUrl('addEbCommerceAddress', {
      urlParams: { userId }
    })
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    })
    return this.http.post<EbAddress>(url, JSON.stringify(newAddress), { headers }).pipe(
      take(1),
      map(newEbAddress => {
        if (newEbAddress.ebCommerceAddress) {
          this.checkoutDeliveryAddressService.setDeliveryAddress(newEbAddress)
        }
        return newEbAddress
      })
    )
  }
  editEbCommerceAddress(
    userId: string,
    addressId: string,
    cartId: string,
    editedAddress: EbAddress
  ): Observable<EbAddress> {
    const url = this.occEndpoints.buildUrl('editEbCommerceAddress', {
      urlParams: { userId, addressId }
    })
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    })
    return this.http.put<EbAddress>(url, JSON.stringify(editedAddress), { headers }).pipe(
      take(1),
      map(newEbAddress => {
        this.checkoutDeliveryAddressService.setDeliveryAddress(newEbAddress)
        return newEbAddress
      })
    )
  }
  deleteEbCommerceAddress(addressId: string, userId: string): Observable<boolean>{
    const url = this.occEndpoints.buildUrl('deleteEbCommerceAddress',{
      urlParams: { userId, addressId }
    })
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    })
    return this.http.delete<boolean>(url, { headers })
  }
}
